import type {ExtrudeFeatureEditDefinition as ExtrudePropertyDefinition} from '@puppycad/cad-core';

const MIN_EXTRUDE_DRAG_DEPTH = 1e-6;
const DEFAULT_EXTRUDE_DRAG_DEPTH = 0.01;

/**
 * 拉伸手柄侧别。
 */
export type ExtrudeHandleSide = 'positive' | 'negative';

/**
 * 拉伸手柄角色。
 */
export type ExtrudeHandleRole = 'primary' | 'secondary';

/**
 * 拉伸拖拽状态。
 *
 * 说明：
 * 1. Direction 1 与 Direction 2 在交互层统一抽象为“草图平面两侧的两段深度”；
 * 2. direction1Reverse 只决定主侧位于正法向还是反法向；
 * 3. direction2 永远位于主侧的对侧，不再把它看成第二个独立特征。
 */
export interface ExtrudeDirectionDragState {
  /** Direction 1 深度。 */
  direction1Depth: number;
  /** Direction 1 是否反向。 */
  direction1Reverse: boolean;
  /** Direction 2 是否启用。 */
  direction2Enabled: boolean;
  /** Direction 2 深度。 */
  direction2Depth: number;
  /** 两侧对称时，direction1Depth 表示两侧总深度。 */
  isMidPlane?: boolean;
  /** 引用型终止条件不能被深度手柄改写。 */
  primaryDepthEditable?: boolean;
  /** 第二方向独立控制深度手柄是否可编辑。 */
  secondaryDepthEditable?: boolean;
}

/**
 * 双侧手柄布局。
 */
export interface ExtrudeDirectionHandleLayout {
  /** 正法向一侧深度。 */
  positiveLength: number;
  /** 反法向一侧深度。 */
  negativeLength: number;
  /** 正法向一侧的手柄角色。 */
  positiveRole: ExtrudeHandleRole | null;
  /** 反法向一侧的手柄角色。 */
  negativeRole: ExtrudeHandleRole | null;
  /** 主侧所在位置。 */
  primarySide: ExtrudeHandleSide;
}

/**
 * 归一化拖拽深度。
 *
 * @param value 输入值。
 * @returns 合法深度。
 */
function normalizeDragDepth(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_EXTRUDE_DRAG_DEPTH;
  }
  // 深度使用米存储，属性与手柄同步必须保真；只有实际鼠标拖动才应用交互步进。
  return Math.max(MIN_EXTRUDE_DRAG_DEPTH, Math.abs(value));
}

/**
 * 克隆拖拽状态。
 *
 * @param state 原始状态。
 * @returns 克隆结果。
 */
export function cloneExtrudeDirectionDragState(
  state: ExtrudeDirectionDragState,
): ExtrudeDirectionDragState {
  return {
    direction1Depth: state.direction1Depth,
    direction1Reverse: state.direction1Reverse,
    direction2Enabled: state.direction2Enabled,
    direction2Depth: state.direction2Depth,
    isMidPlane: state.isMidPlane,
    primaryDepthEditable: state.primaryDepthEditable,
    secondaryDepthEditable: state.secondaryDepthEditable,
  };
}

/**
 * 从属性定义构建拖拽状态。
 *
 * @param definition 拉伸属性定义。
 * @returns 拖拽状态。
 */
export function buildExtrudeDirectionDragState(
  definition: ExtrudePropertyDefinition,
): ExtrudeDirectionDragState {
  return {
    direction1Depth: normalizeDragDepth(definition.direction1.depth),
    direction1Reverse: Boolean(definition.direction1.reverse),
    direction2Enabled: Boolean(definition.direction2.enabled),
    direction2Depth: normalizeDragDepth(definition.direction2.depth),
    isMidPlane: definition.direction1.endCondition === 'mid_plane',
    primaryDepthEditable: ['blind', 'mid_plane'].includes(definition.direction1.endCondition),
    secondaryDepthEditable: definition.direction2.endCondition === 'blind',
  };
}

/**
 * 将拖拽状态回写到属性定义。
 *
 * @param definition 原始属性定义。
 * @param state 拖拽状态。
 * @returns 新的属性定义。
 */
export function applyExtrudeDirectionDragState(
  definition: ExtrudePropertyDefinition,
  state: ExtrudeDirectionDragState,
): ExtrudePropertyDefinition {
  return {
    ...definition,
    direction1: {
      ...definition.direction1,
      depth: state.primaryDepthEditable === false ? definition.direction1.depth : normalizeDragDepth(state.direction1Depth),
      reverse: state.direction1Reverse,
    },
    direction2: {
      ...definition.direction2,
      enabled: state.isMidPlane ? false : state.direction2Enabled,
      depth: state.secondaryDepthEditable === false ? definition.direction2.depth : normalizeDragDepth(state.direction2Depth),
      reverse: false,
    },
  };
}

/**
 * 解析当前双侧手柄布局。
 *
 * @param state 拖拽状态。
 * @returns 手柄布局。
 */
export function resolveExtrudeDirectionHandleLayout(
  state: ExtrudeDirectionDragState,
  previewLengths?: readonly [number, number],
): ExtrudeDirectionHandleLayout {
  if (state.isMidPlane) {
    const halfDepth = normalizeDragDepth(state.direction1Depth) / 2;
    return { positiveLength: halfDepth, negativeLength: halfDepth, positiveRole: 'primary', negativeRole: 'primary', primarySide: 'positive' };
  }
  const primarySide: ExtrudeHandleSide = state.direction1Reverse ? 'negative' : 'positive';

  const positiveLength = primarySide === 'positive'
    ? normalizeDragDepth(state.direction1Depth)
    : (state.direction2Enabled ? normalizeDragDepth(state.direction2Depth) : 0);
  const negativeLength = primarySide === 'negative'
    ? normalizeDragDepth(state.direction1Depth)
    : (state.direction2Enabled ? normalizeDragDepth(state.direction2Depth) : 0);

  return {
    positiveLength: previewLengths && (primarySide === 'positive' ? state.primaryDepthEditable : state.secondaryDepthEditable) === false ? previewLengths[0] : positiveLength,
    negativeLength: previewLengths && (primarySide === 'negative' ? state.primaryDepthEditable : state.secondaryDepthEditable) === false ? previewLengths[1] : negativeLength,
    positiveRole: positiveLength >= MIN_EXTRUDE_DRAG_DEPTH
      ? (primarySide === 'positive' ? 'primary' : 'secondary')
      : null,
    negativeRole: negativeLength >= MIN_EXTRUDE_DRAG_DEPTH
      ? (primarySide === 'negative' ? 'primary' : 'secondary')
      : null,
    primarySide,
  };
}
